import { useState, useCallback } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreateOrganizationDialog, CreateBusinessUnitDialog, CreateCustomerDialog, CreateProductDialog } from "@/components/dialogs";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { CustomersList, ProductsList } from "@/components/inline-entity-list";
import { ImpersonateButton } from "@/components/impersonate-button";
import { cn } from "@/lib/utils";
import { EntityDrawer, useDrawerEntity } from "@/components/entity-drawer";
import { Plus, Building2, Search, ChevronRight, ChevronDown, Pencil, Trash2, Loader2, Copy, Check, X, Bot } from "lucide-react";
import { COUNTRY_CURRENCY_MAP, COUNTRY_LABELS } from "@shared/schema";
import type { BaseCountry, BaseCurrency } from "@shared/schema";

interface Organization {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
  businessUnitCount: number;
}

interface BusinessUnit {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  cardProgramName: string | null;
  maxCardsPerCustomer: number;
  status: string;
  createdAt: string;
}

interface OrganizationDetailData {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  status: string;
  apiKey: string;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface BusinessUnitDetail {
  id: string;
  name: string;
  slug: string;
  organizationId: string;
  email: string | null;
  phone: string | null;
  status: string;
  cardProgramName: string | null;
  maxCardsPerCustomer: number;
  defaultDailyLimitCents: number | null;
  defaultMonthlyLimitCents: number | null;
  createdAt: string;
  updatedAt: string;
}

interface DrawerEntity {
  type: "organization" | "business-unit";
  id: string;
}

function getStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    ACTIVE: "default",
    INACTIVE: "secondary",
    PENDING: "outline",
    SUSPENDED: "destructive",
  };
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
}

// ─── Organization Detail Content (for drawer) ───────────────────────────────

function OrgDetailContent({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<OrganizationDetailData>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);

  const { data: org, isLoading } = useQuery<OrganizationDetailData>({
    queryKey: ["organization", orgId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/organizations/${orgId}`);
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: !!orgId,
  });

  const { data: products } = useQuery<{ id: string }[]>({
    queryKey: ["pm-products", orgId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/products?organizationId=${orgId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!orgId,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<OrganizationDetailData>) => {
      const response = await apiRequest("PATCH", `/api/organizations/${orgId}`, updates);
      if (!response.ok) throw new Error("Failed to update");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", orgId] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Organization updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update organization", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/organizations/${orgId}`);
      if (!response.ok) throw new Error("Failed to delete");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast({ title: "Success", description: "Organization deleted successfully" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete organization", variant: "destructive" });
    },
  });

  const syncProductMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/automation/sync-product", {
        organization_id: orgId,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = typeof err.error === "string" ? err.error : "Sync failed";
        throw new Error(msg);
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["pm-products", orgId] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: `Product ${data.action}`,
        description: `"${data.product?.name}" synced from iServeU portal (${data.fields_synced} fields)`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Sync failed", description: error.message, variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (org) {
      setEditForm({
        name: org.name, email: org.email || "", phone: org.phone || "", status: org.status,
        settings: {
          ...org.settings,
          baseCountry: (org.settings?.baseCountry as BaseCountry) || 'AU',
          baseCurrency: (org.settings?.baseCurrency as BaseCurrency) || 'AUD',
        },
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    const payload = { ...editForm };
    if (payload.settings && org?.settings) {
      payload.settings = { ...org.settings, ...payload.settings };
    }
    updateMutation.mutate(payload);
  };
  const handleCancelEdit = () => { setIsEditing(false); setEditForm({}); };

  const copyApiKey = async () => {
    if (org?.apiKey) {
      await navigator.clipboard.writeText(org.apiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
      toast({ title: "Copied", description: "API key copied to clipboard" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!org) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Organization</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{org.name}</h2>
            {getStatusBadge(org.status)}
          </div>
          <p className="text-sm text-muted-foreground">{org.slug}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ImpersonateButton
            entityType="ORGANIZATION"
            entityId={org.id}
            entityName={org.name}
            variant="outline"
            size="sm"
          />
          {!isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Delete
              </Button>
            </>
          )}
        </div>

        <div className="border-t" />

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Base Country</Label>
                <Select
                  value={(editForm.settings?.baseCountry as string) || 'AU'}
                  onValueChange={(v: string) => {
                    const country = v as BaseCountry;
                    const currencies = COUNTRY_CURRENCY_MAP[country];
                    setEditForm({
                      ...editForm,
                      settings: {
                        ...editForm.settings,
                        baseCountry: country,
                        baseCurrency: currencies[0],
                      },
                    });
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.entries(COUNTRY_LABELS) as [BaseCountry, string][]).map(([code, label]) => (
                      <SelectItem key={code} value={code}>{label} ({code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Base Currency</Label>
                <Select
                  value={(editForm.settings?.baseCurrency as string) || 'AUD'}
                  onValueChange={(v: string) =>
                    setEditForm({
                      ...editForm,
                      settings: { ...editForm.settings, baseCurrency: v as BaseCurrency },
                    })
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CURRENCY_MAP[((editForm.settings?.baseCountry as BaseCountry) || 'AU')].map((cur) => (
                      <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="products">Products ({products?.length || 0})</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <DetailCard title="Basic Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Name" value={org.name} />
                  <DetailItem label="Slug" value={org.slug} />
                  <DetailItem label="Email" value={org.email || "-"} />
                  <DetailItem label="Phone" value={org.phone || "-"} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Regional Settings">
                <DetailGrid columns={2}>
                  <DetailItem
                    label="Base Country"
                    value={COUNTRY_LABELS[(org.settings?.baseCountry as BaseCountry) || 'AU'] || 'Australia'}
                  />
                  <DetailItem
                    label="Base Currency"
                    value={(org.settings?.baseCurrency as string) || 'AUD'}
                  />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="API Access">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">API Key</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-muted px-3 py-2 rounded break-all font-mono">
                      {org.apiKey}
                    </code>
                    <Button variant="outline" size="sm" onClick={copyApiKey}>
                      {apiKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </DetailCard>

              <DetailCard title="Record Information">
                <DetailGrid columns={1}>
                  <DetailItem label="ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{org.id}</code>} />
                  <DetailItem label="Created" value={new Date(org.createdAt).toLocaleString()} />
                  <DetailItem label="Last Updated" value={new Date(org.updatedAt).toLocaleString()} />
                </DetailGrid>
              </DetailCard>
            </TabsContent>

            <TabsContent value="products" className="space-y-4">
              {/* Sync from iServeU (SUPER only) */}
              {user?.role === "SUPER" && (
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">iServeU Portal Sync</p>
                    <p className="text-xs text-muted-foreground">
                      {syncProductMutation.isPending
                        ? "Fetching from iServeU portal... 1–3 minutes"
                        : "Import product config, BIN, limits & velocity rules"}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 ml-3"
                    disabled={syncProductMutation.isPending}
                    onClick={() => syncProductMutation.mutate()}
                  >
                    {syncProductMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Bot className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {syncProductMutation.isPending ? "Syncing..." : "Sync from iServeU"}
                  </Button>
                </div>
              )}

              <ProductsList organizationId={org.id} onCreate={() => setCreateProductOpen(true)} />
            </TabsContent>

            <TabsContent value="activity">
              <ActivityTab entityType="organization" entityId={org.id} createdAt={org.createdAt} updatedAt={org.updatedAt} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{org.name}&quot;? This action cannot be undone and will also delete all associated admins, products, and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateProductDialog open={createProductOpen} onOpenChange={setCreateProductOpen} organizationId={orgId} />
    </>
  );
}

// ─── Business Unit Detail Content (for drawer) ─────────────────────────

function BusinessUnitDetailContent({ adminId, onClose }: { adminId: string; onClose: () => void }) {
  const { toast } = useToast();
  const { formatCurrency } = useOrgSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<BusinessUnitDetail>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);

  const { data: admin, isLoading } = useQuery<BusinessUnitDetail>({
    queryKey: ["business-unit", adminId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/business-units/${adminId}`);
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: !!adminId,
  });

  const { data: customers } = useQuery<{ id: string }[]>({
    queryKey: ["bu-customers", adminId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/customers?businessUnitId=${adminId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!adminId,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<BusinessUnitDetail>) => {
      const response = await apiRequest("PATCH", `/api/business-units/${adminId}`, updates);
      if (!response.ok) throw new Error("Failed to update");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-unit", adminId] });
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      queryClient.invalidateQueries({ queryKey: ["pm-business-units"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Business unit updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update business unit", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/business-units/${adminId}`);
      if (!response.ok) throw new Error("Failed to delete");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-units"] });
      queryClient.invalidateQueries({ queryKey: ["pm-business-units"] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast({ title: "Success", description: "Business unit deleted successfully" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete business unit", variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (admin) {
      setEditForm({
        name: admin.name, email: admin.email || "", phone: admin.phone || "",
        cardProgramName: admin.cardProgramName || "", maxCardsPerCustomer: admin.maxCardsPerCustomer,
        defaultDailyLimitCents: admin.defaultDailyLimitCents, defaultMonthlyLimitCents: admin.defaultMonthlyLimitCents,
        status: admin.status,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => updateMutation.mutate(editForm);
  const handleCancelEdit = () => { setIsEditing(false); setEditForm({}); };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!admin) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-primary/60" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Business Unit</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{admin.name}</h2>
            {getStatusBadge(admin.status)}
          </div>
          <p className="text-sm text-muted-foreground">{admin.cardProgramName || admin.slug}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <ImpersonateButton
            entityType="BUSINESS_UNIT"
            entityId={admin.id}
            entityName={admin.name}
            variant="outline"
            size="sm"
          />
          {!isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Delete
              </Button>
            </>
          )}
        </div>

        <div className="border-t" />

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Card Program Name</Label>
                <Input value={editForm.cardProgramName || ""} onChange={(e) => setEditForm({ ...editForm, cardProgramName: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Max Cards Per Customer</Label>
                <Input type="number" value={editForm.maxCardsPerCustomer || 1} onChange={(e) => setEditForm({ ...editForm, maxCardsPerCustomer: parseInt(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label>Default Daily Limit (cents)</Label>
                <Input type="number" value={editForm.defaultDailyLimitCents ?? ""} onChange={(e) => setEditForm({ ...editForm, defaultDailyLimitCents: e.target.value ? parseInt(e.target.value) : null })} placeholder="No limit" />
              </div>
              <div className="space-y-2">
                <Label>Default Monthly Limit (cents)</Label>
                <Input type="number" value={editForm.defaultMonthlyLimitCents ?? ""} onChange={(e) => setEditForm({ ...editForm, defaultMonthlyLimitCents: e.target.value ? parseInt(e.target.value) : null })} placeholder="No limit" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="customers">Customers ({customers?.length || 0})</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <DetailCard title="Basic Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Company Name" value={admin.name} />
                  <DetailItem label="Slug" value={admin.slug} />
                  <DetailItem label="Card Program" value={admin.cardProgramName || "-"} />
                  <DetailItem label="Email" value={admin.email || "-"} />
                  <DetailItem label="Phone" value={admin.phone || "-"} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Card Configuration">
                <DetailGrid columns={1}>
                  <DetailItem label="Max Cards Per Customer" value={admin.maxCardsPerCustomer.toString()} />
                  <DetailItem label="Default Daily Limit" value={formatCurrency(admin.defaultDailyLimitCents)} />
                  <DetailItem label="Default Monthly Limit" value={formatCurrency(admin.defaultMonthlyLimitCents)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Record Information">
                <DetailGrid columns={1}>
                  <DetailItem label="ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{admin.id}</code>} />
                  <DetailItem label="Organization ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{admin.organizationId}</code>} />
                  <DetailItem label="Created" value={new Date(admin.createdAt).toLocaleString()} />
                  <DetailItem label="Last Updated" value={new Date(admin.updatedAt).toLocaleString()} />
                </DetailGrid>
              </DetailCard>
            </TabsContent>

            <TabsContent value="customers">
              <CustomersList businessUnitId={admin.id} onCreate={() => setCreateCustomerOpen(true)} />
            </TabsContent>

            <TabsContent value="activity">
              <ActivityTab entityType="business_unit" entityId={admin.id} createdAt={admin.createdAt} updatedAt={admin.updatedAt} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Business Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{admin.name}&quot;? This action cannot be undone and will also delete all associated customers, cards, and transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateCustomerDialog open={createCustomerOpen} onOpenChange={setCreateCustomerOpen} businessUnitId={adminId} />
    </>
  );
}

// ─── Business Unit Rows (accordion children) ────────────────────────────────

function BusinessUnitRows({
  organizationId,
  selectedAdminId,
  onAdminClick,
}: {
  organizationId: string;
  selectedAdminId: string | null;
  onAdminClick: (adminId: string) => void;
}) {
  const [createAdminOpen, setCreateAdminOpen] = useState(false);

  const { data: admins, isLoading } = useQuery<BusinessUnit[]>({
    queryKey: ["pm-business-units", organizationId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/business-units?organizationId=${organizationId}`);
      if (!response.ok) return [];
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={7} className="p-0">
          <div className="pl-12 pr-4 py-3 bg-muted/30 border-l-2 border-primary/20 ml-4">
            <Skeleton className="h-8 w-full" />
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {admins?.map((admin) => (
        <TableRow
          key={admin.id}
          className={cn(
            "cursor-pointer hover:bg-muted/50 bg-muted/20",
            selectedAdminId === admin.id && "bg-primary/10 hover:bg-primary/15 border-l-2 border-primary"
          )}
          onClick={(e) => { e.stopPropagation(); onAdminClick(admin.id); }}
        >
          <TableCell className="pl-12">
            <div className="flex items-center gap-2">
              <div className={cn("w-1.5 h-1.5 rounded-full", selectedAdminId === admin.id ? "bg-primary" : "bg-primary/40")} />
              <span className="font-medium">{admin.name}</span>
            </div>
          </TableCell>
          <TableCell>{admin.cardProgramName || "-"}</TableCell>
          <TableCell>{admin.email || "-"}</TableCell>
          <TableCell>{getStatusBadge(admin.status)}</TableCell>
          <TableCell />
          <TableCell>{new Date(admin.createdAt).toLocaleDateString()}</TableCell>
          <TableCell />
        </TableRow>
      ))}
      <TableRow className="bg-muted/20 hover:bg-muted/30">
        <TableCell colSpan={7} className="pl-12">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); setCreateAdminOpen(true); }}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Business Unit
          </Button>
        </TableCell>
      </TableRow>
      <CreateBusinessUnitDialog
        open={createAdminOpen}
        onOpenChange={setCreateAdminOpen}
        organizationId={organizationId}
      />
    </>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function OrganizationView() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const { drawerEntity, setDrawerEntity, displayEntity } = useDrawerEntity<DrawerEntity>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const canCreate = user?.role === "SUPER";

  const { data: organizations, isLoading } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/organizations");
      if (!response.ok) throw new Error("Failed to fetch organizations");
      return response.json();
    },
  });

  const handleOrgClick = useCallback((orgId: string) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
        setDrawerEntity(prev => prev?.type === "organization" && prev.id === orgId ? null : prev);
      } else {
        next.add(orgId);
        setDrawerEntity({ type: "organization", id: orgId });
      }
      return next;
    });
  }, []);

  const handleAdminClick = useCallback((adminId: string) => {
    setDrawerEntity(prev =>
      prev?.type === "business-unit" && prev.id === adminId
        ? null
        : { type: "business-unit", id: adminId }
    );
  }, []);

  const filteredOrgs = organizations?.filter((org) => {
    if (!showInactive && org.status !== "ACTIVE") return false;
    return (
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div className="relative min-h-screen">
      {/* Main content area */}
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Organizations</h1>
            <p className="text-muted-foreground">Manage organizations and their business units</p>
          </div>
          {canCreate && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Organizations</CardTitle>
                <CardDescription>
                  {filteredOrgs?.length ?? 0} organization{(filteredOrgs?.length ?? 0) !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
                  <Label htmlFor="show-inactive" className="text-sm text-muted-foreground whitespace-nowrap">
                    Show Inactive
                  </Label>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredOrgs?.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No organizations</h3>
                <p className="text-muted-foreground">Get started by creating a new organization.</p>
                {canCreate && (
                  <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Organization
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email / Card Program</TableHead>
                    <TableHead>Phone / Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Business Units</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrgs?.map((org) => {
                    const isExpanded = expandedOrgs.has(org.id);
                    const isSelected = drawerEntity?.type === "organization" && drawerEntity.id === org.id;
                    return (
                      <>
                        <TableRow
                          key={org.id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            isSelected && "bg-primary/10 hover:bg-primary/15"
                          )}
                          onClick={() => handleOrgClick(org.id)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              {org.name}
                            </div>
                          </TableCell>
                          <TableCell>{org.email || "-"}</TableCell>
                          <TableCell>{org.phone || "-"}</TableCell>
                          <TableCell>{getStatusBadge(org.status)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{org.businessUnitCount ?? 0}</Badge>
                          </TableCell>
                          <TableCell>{new Date(org.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell />
                        </TableRow>
                        {isExpanded && (
                          <BusinessUnitRows
                            organizationId={org.id}
                            selectedAdminId={drawerEntity?.type === "business-unit" ? drawerEntity.id : null}
                            onAdminClick={handleAdminClick}
                          />
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <CreateOrganizationDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      </div>

      {/* Right-side detail drawer */}
      <EntityDrawer isOpen={!!drawerEntity} onClose={() => setDrawerEntity(null)}>
        {displayEntity?.type === "organization" && (
          <OrgDetailContent key={displayEntity.id} orgId={displayEntity.id} onClose={() => setDrawerEntity(null)} />
        )}
        {displayEntity?.type === "business-unit" && (
          <BusinessUnitDetailContent key={displayEntity.id} adminId={displayEntity.id} onClose={() => setDrawerEntity(null)} />
        )}
      </EntityDrawer>
    </div>
  );
}
